import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  mapSecurityAlertDetailResponseToDto,
  mapSecurityAlertsListResponseToDto,
} from '../dto/security-alerts.dto.js';

const PAGE_LIMIT = 50;
const DEFAULT_FILTERS = {
  status: 'open',
  severity: '',
};

function formatDate(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatEnumLabel(value) {
  return String(value || '-')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getSeverityBadgeClass(severity) {
  switch (severity) {
    case 'critical':
      return 'bg-danger text-white';
    case 'high':
      return 'bg-warning-subtle text-warning';
    case 'medium':
      return 'bg-info-subtle text-info';
    default:
      return 'bg-secondary-subtle text-secondary';
  }
}

function getStatusBadgeClass(status) {
  switch (status) {
    case 'open':
      return 'bg-danger-subtle text-danger';
    case 'acknowledged':
      return 'bg-warning-subtle text-warning';
    case 'resolved':
      return 'bg-success-subtle text-success';
    case 'false_positive':
      return 'bg-secondary-subtle text-secondary';
    default:
      return 'bg-light text-muted';
  }
}

function buildAlertPresentation(alert) {
  const ruleLabel = alert.ruleName || alert.ruleId || '-';
  const actorLabel = alert.actorProfileUid || '-';

  return {
    ...alert,
    key: alert.uid,
    ruleLabel,
    actorLabel,
    createdAtLabel: formatDate(alert.createdAt),
    updatedAtLabel: formatDate(alert.updatedAt),
    windowStartLabel: formatDate(alert.windowStart),
    windowEndLabel: formatDate(alert.windowEnd),
    windowLabel: `${formatDate(alert.windowStart)} - ${formatDate(alert.windowEnd)}`,
    severityLabel: formatEnumLabel(alert.severity),
    severityBadgeClass: getSeverityBadgeClass(alert.severity),
    statusLabel: formatEnumLabel(alert.status),
    statusBadgeClass: getStatusBadgeClass(alert.status),
    ip: alert.ip || '-',
    userAgent: alert.userAgent || '-',
  };
}

function buildQuery(filters, page) {
  return {
    limit: PAGE_LIMIT,
    offset: page * PAGE_LIMIT,
    status: filters.status || undefined,
    severity: filters.severity || undefined,
  };
}

export function useSecurityAlerts() {
  const { token, hasPermission } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const filtersRef = useRef(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailError, setDetailError] = useState('');
  const [detailAlert, setDetailAlert] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const statusNoteRef = useRef('');

  const canManageSecurityAlerts = Boolean(hasPermission?.('security_alert.manage'));

  const loadAlerts = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.getSecurityAlerts(token, buildQuery(filters, page));
      const dto = mapSecurityAlertsListResponseToDto(response);
      setAlerts(dto.alerts);
      setTotal(dto.total);
    } catch (requestError) {
      setAlerts([]);
      setTotal(0);
      setError(requestError?.message || 'Failed to load security alerts.');
    } finally {
      setIsLoading(false);
    }
  }, [token, filters, page]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const updateFilter = useCallback((name, value) => {
    filtersRef.current = {
      ...filtersRef.current,
      [name]: value,
    };
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
    setPage(0);
  }, []);

  const applyFilters = useCallback(() => {
    setFilters(filtersRef.current);
    setPage(0);
  }, []);

  const resetFilters = useCallback(() => {
    filtersRef.current = DEFAULT_FILTERS;
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  }, []);

  const refresh = useCallback(() => {
    setSuccess('');
    return loadAlerts();
  }, [loadAlerts]);

  const openDetail = useCallback(async (alert) => {
    const uid = alert?.uid;
    if (!uid) {
      return;
    }

    setIsDetailOpen(true);
    setDetailAlert(buildAlertPresentation(alert));
    setDetailError('');
    statusNoteRef.current = '';
    setStatusNote('');
    setIsDetailLoading(true);

    try {
      const response = await adminApi.getSecurityAlert(token, uid);
      setDetailAlert(buildAlertPresentation(mapSecurityAlertDetailResponseToDto(response)));
    } catch (requestError) {
      setDetailError(requestError?.message || 'Failed to load security alert detail.');
    } finally {
      setIsDetailLoading(false);
    }
  }, [token]);

  const closeDetail = useCallback(() => {
    setIsDetailOpen(false);
    setDetailAlert(null);
    setDetailError('');
    statusNoteRef.current = '';
    setStatusNote('');
  }, []);

  const updateStatusNote = useCallback((value) => {
    statusNoteRef.current = value;
    setStatusNote(value);
  }, []);

  const updateAlertStatus = useCallback(async (status) => {
    if (!detailAlert?.uid) {
      return false;
    }

    setIsStatusSaving(true);
    setDetailError('');
    setSuccess('');

    const note = String(statusNoteRef.current || '').trim();
    const payload = {
      status,
      note: note || null,
    };

    try {
      const response = await adminApi.updateSecurityAlertStatus(token, detailAlert.uid, payload);
      const updatedAlert = mapSecurityAlertDetailResponseToDto(response);
      const presentedAlert = buildAlertPresentation(updatedAlert);
      setDetailAlert(presentedAlert);
      setAlerts((current) => current.map((item) => (item.uid === updatedAlert.uid ? updatedAlert : item)));
      setSuccess('Security alert status updated.');
      return true;
    } catch (requestError) {
      setDetailError(requestError?.message || 'Failed to update security alert status.');
      return false;
    } finally {
      setIsStatusSaving(false);
    }
  }, [token, detailAlert]);

  const rows = useMemo(() => alerts.map(buildAlertPresentation), [alerts]);

  return {
    alerts,
    rows,
    total,
    filters,
    page,
    isLoading,
    isDetailLoading,
    isStatusSaving,
    error,
    success,
    detailError,
    detailAlert,
    isDetailOpen,
    statusNote,
    canManageSecurityAlerts,
    updateFilter,
    applyFilters,
    resetFilters,
    refresh,
    openDetail,
    closeDetail,
    updateStatusNote,
    updateAlertStatus,
  };
}
